import {
  Box,
  Checkbox,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React from 'react';

import { UsageGraphMode } from '@/Units/types';

import { naturalCompare } from '../../util';

/**
 * Minimal shape of a unit row rendered in this table. Both `UnitBilling`
 * (from `bill.units`) and the ad-hoc `Unit` interface used in BreakdownDialog
 * satisfy this contract.
 */
export interface UsageGraphModeTableUnit {
  id: string;
  name: string;
  is_billable?: boolean;
  usage_graph_mode?: number;
}

interface UsageGraphModeTableProps {
  /** All units for the billing; non-billable units are filtered out internally. */
  units: UsageGraphModeTableUnit[];
  /**
   * Per-unit overrides for this batch, keyed by unit id. Values are
   * `UsageGraphMode` ints. Only units whose effective value differs from the
   * unit's stored default should appear here.
   */
  overrides: Record<string, number>;
  onOverridesChange: (next: Record<string, number>) => void;
  /** Whether the "save as new default" checkbox is checked. */
  saveAsDefault: boolean;
  onSaveAsDefaultChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Outer box sx override for callers that need extra padding (e.g. Stripe dialog). */
  sx?: React.ComponentProps<typeof Box>['sx'];
}

/**
 * Resolve the effective `usage_graph_mode` for a unit in the batch dialog:
 * batch override > unit's stored value > INCLUDED.
 */
const getEffectiveGraphMode = (
  unit: UsageGraphModeTableUnit,
  overrides: Record<string, number>,
): number => {
  if (unit.id in overrides) return overrides[unit.id];
  return unit.usage_graph_mode ?? UsageGraphMode.INCLUDED;
};

/**
 * Per-unit "Usage Graph on Invoices" toggle table, shared between the NonBilling
 * "Generate Invoices" dialog and the Stripe "Send Invoices" confirmation dialog.
 *
 * Renders one row per billable unit (sorted naturally by name) with a checkbox
 * that toggles whether that unit's generated custom invoice PDF will include
 * the daily usage bar chart. A header checkbox provides "all on / all off /
 * indeterminate" selection. When any unit differs from its stored default, an
 * additional "save as new default" checkbox is shown.
 *
 * This component is fully controlled — the parent owns `overrides` and
 * `saveAsDefault` state so it can thread both into the batch generate request.
 * Returns `null` if there are no billable units.
 */
export const UsageGraphModeTable: React.FC<UsageGraphModeTableProps> = ({
  units,
  overrides,
  onOverridesChange,
  saveAsDefault,
  onSaveAsDefaultChange,
  disabled = false,
  sx,
}) => {
  const billableUnits = units
    .filter((u) => u.is_billable !== false)
    .slice()
    .sort((a, b) => naturalCompare(a.name, b.name));

  if (billableUnits.length === 0) return null;

  const effectiveValues = billableUnits.map(
    (u) => getEffectiveGraphMode(u, overrides) === UsageGraphMode.INCLUDED,
  );
  const allOn = effectiveValues.every((v) => v);
  const noneOn = effectiveValues.every((v) => !v);
  const indeterminate = !allOn && !noneOn;

  const handleToggleUnit = (unit: UsageGraphModeTableUnit, checked: boolean) => {
    const newMode = checked ? UsageGraphMode.INCLUDED : UsageGraphMode.HIDDEN;
    const storedDefault = unit.usage_graph_mode ?? UsageGraphMode.INCLUDED;
    const next = { ...overrides };
    if (newMode === storedDefault) {
      delete next[unit.id];
    } else {
      next[unit.id] = newMode;
    }
    onOverridesChange(next);
  };

  const handleToggleAll = (checked: boolean) => {
    const newMode = checked ? UsageGraphMode.INCLUDED : UsageGraphMode.HIDDEN;
    const next: Record<string, number> = {};
    for (const u of billableUnits) {
      const storedDefault = u.usage_graph_mode ?? UsageGraphMode.INCLUDED;
      if (newMode !== storedDefault) {
        next[u.id] = newMode;
      }
    }
    onOverridesChange(next);
  };

  return (
    <Box sx={sx}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Usage Graph per Unit
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Toggle the daily usage bar chart for each unit on the generated SSW invoice PDFs. Helpful
        when meter data is incomplete. Estimated usage entered by Support renders on the chart
        automatically as a striped segment above the metered bars.
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allOn}
                  indeterminate={indeterminate}
                  onChange={(e) => handleToggleAll(e.target.checked)}
                  disabled={disabled}
                  inputProps={{ 'aria-label': 'toggle usage graph for all units' }}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Usage Graph
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {billableUnits.map((u) => {
              const checked = getEffectiveGraphMode(u, overrides) === UsageGraphMode.INCLUDED;
              return (
                <TableRow key={u.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={checked}
                      onChange={(e) => handleToggleUnit(u, e.target.checked)}
                      disabled={disabled}
                      inputProps={{
                        'aria-label': `toggle usage graph for unit ${u.name}`,
                      }}
                    />
                  </TableCell>
                  <TableCell>{u.name}</TableCell>
                  <TableCell align="right">
                    <Typography variant="caption" color="text.secondary">
                      {checked ? 'Included' : 'Hidden'}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {Object.keys(overrides).length > 0 && (
        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Checkbox
              checked={saveAsDefault}
              onChange={(e) => onSaveAsDefaultChange(e.target.checked)}
              disabled={disabled}
            />
          }
          label="Save these as the new defaults for these units"
        />
      )}
    </Box>
  );
};
